import React, { useEffect } from 'react';
import { CheckCircle, AlertCircle, X, Info, AlertTriangle } from 'lucide-react';

const ToastMessage = ({ message, type = 'success', onClose, duration = 4000 }) => {
  // Configure icon and colors based on toast type
  const toastConfig = {
    success: {
      icon: <CheckCircle size={20} />,
      bgColor: 'bg-green-100',
      textColor: 'text-green-800',
      borderColor: 'border-green-500',
      iconColor: 'text-green-500'
    },
    error: {
      icon: <AlertCircle size={20} />,
      bgColor: 'bg-red-100',
      textColor: 'text-red-800',
      borderColor: 'border-red-500',
      iconColor: 'text-red-500'
    },
    warning: {
      icon: <AlertTriangle size={20} />,
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-800',
      borderColor: 'border-yellow-500',
      iconColor: 'text-yellow-500'
    },
    info: {
      icon: <Info size={20} />,
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-800',
      borderColor: 'border-blue-500',
      iconColor: 'text-blue-500'
    }
  };
  
  // Always declare useEffect, regardless of whether message exists
  useEffect(() => {
    let timer;
    if (message && onClose) {
      timer = setTimeout(() => {
        onClose();
      }, duration);
    }
    
    // Cleanup function to clear timeout if component unmounts
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [message, onClose, duration]);
  
  // Return null if no message to display, but only after hooks are called
  if (!message) return null;
  
  const config = toastConfig[type] || toastConfig.success;
  
  return (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4">
      <div 
        className={`${config.bgColor} ${config.textColor} rounded-lg shadow-lg border-l-4 ${config.borderColor} flex items-center justify-between p-4`}
        style={{
          animation: 'slideIn 0.3s ease-out forwards',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }}
      >
        <div className="flex items-center space-x-3">
          <div className={config.iconColor}>
            {config.icon}
          </div>
          <p className="font-medium">{message}</p>
        </div>
        <button 
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 transition-colors focus:outline-none"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>
      
      <style jsx global>{`
        @keyframes slideIn {
          from {
            transform: translateY(-20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default ToastMessage;